/**
 * Browser-safe JS mirror of the canonical waste-stream amount rule.
 *
 * The SQL half of the rule lives in ./effectiveAmount.ts, which imports the
 * Prisma client and therefore must never be imported from Oak — the generated
 * client references `process` and crashes the browser at module init. Keep
 * this module dependency-free so frontend code can share the rule.
 */

export type EManifestAmountLine = {
  eManifestAmount?: number | null;
  /** Staff-confirmed Box 18a amount in pounds; used instead of eManifestAmount when set and eManifestAmount is not null. */
  eManifestAmountOverride?: number | null;
};

export type EffectiveAmountLine = EManifestAmountLine & {
  vendorAmount: number | null;
  /**
   * Whether the line's source document has been human-reviewed. Derive this
   * from the live DocumentMetadata state (needsReview = false AND reviewedAt
   * set), matching the SQL fragment's EXISTS — NOT from the denormalized
   * WasteLineUnified.documentReviewed column, which is refreshed by a queued
   * process-manifests run and can lag a just-completed review.
   */
  documentReviewed?: boolean;
  documentAmount?: number | null;
};

/** JS mirror of eManifestLineAmountSql: null without a Box 11 amount, else the override when set, else Box 11. */
export function eManifestLineAmount(line: EManifestAmountLine): number | null {
  return line.eManifestAmount == null
    ? null
    : (line.eManifestAmountOverride ?? line.eManifestAmount);
}

/**
 * The canonical per-line effective amount (see effectiveAmount.ts module doc):
 * eManifest → vendor → reviewed document; zero = not reported.
 */
export function effectiveLineAmount(line: EffectiveAmountLine): number {
  const eManifest = eManifestLineAmount(line);
  if (eManifest) return eManifest;
  if (line.vendorAmount) return line.vendorAmount;
  if (line.documentReviewed && line.documentAmount) return line.documentAmount;
  return 0;
}
